import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Box, Text, Title } from '@factorialco/deprecated-design-system'
import { FindPolicyCategoryWorkflowDocument } from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'
import { WorkflowProvider } from 'modules/workflows/contexts/WorkflowProvider'

// Default workflow ID for testing - can be overridden via ?workflowId= query param
const DEFAULT_WORKFLOW_ID = 5

function PolicyCategoryWorkflowPageContent() {
  const { company } = useCurrentSession()
  const [searchParams] = useSearchParams()

  const workflowIdParam = searchParams.get('workflowId')
  const workflowId = workflowIdParam ? parseInt(workflowIdParam, 10) : DEFAULT_WORKFLOW_ID

  const { data, loading, error } = useQuery(FindPolicyCategoryWorkflowDocument, {
    variables: {
      companyId: company.id,
      workflowId: workflowId,
      featureNames: ['dev_expenses_policy_settings'],
    },
    skip: !company?.id || isNaN(workflowId),
  })

  const workflow = data?.workflows?.workflowsConnection?.nodes?.[0]

  const isFeatureEnabled =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_expenses_policy_settings'
    )?.enabled ?? false

  // Variables config for the workflow - maps to workflow_data_type fields
  const variablesConfig: VariablesConfig = useMemo(
    () => ({
      expense_amount: {
        component: 'number',
        valueType: 'number',
        label: 'Expense amount',
      },
      aggregated_spent_amount_per_day: {
        component: 'number',
        valueType: 'number',
        label: 'Aggregated spent per day',
      },
      aggregated_spent_amount_per_week: {
        component: 'number',
        valueType: 'number',
        label: 'Aggregated spent per week',
      },
      aggregated_spent_amount_per_month: {
        component: 'number',
        valueType: 'number',
        label: 'Aggregated spent per month',
      },
      aggregated_spent_amount_per_year: {
        component: 'number',
        valueType: 'number',
        label: 'Aggregated spent per year',
      },
    }),
    []
  )

  if (loading) {
    return (
      <ResponsivePage.Body loading error={null} showHeader={false} variant="fullscreen">
        <></>
      </ResponsivePage.Body>
    )
  }

  if (!isFeatureEnabled) {
    return <NotFound />
  }

  return (
    <ResponsivePage.Body loading={false} error={error} showHeader={false} variant="fullscreen">
      <Box gap="s16" paddingX="s24" paddingY="s16">
        <Title title="Policy Category Evaluation Workflow" variant="400" />
      </Box>

      {workflow && (
        <WorkflowContainer
          handlers={{}}
          heightOffset="180px"
          trigger={{
            title: 'When expense is evaluated',
            description: 'Triggered when an expense needs policy category evaluation',
          }}
          variables={variablesConfig}
          workflow={workflow}
        />
      )}

      {!workflow && !loading && (
        <Box paddingX="s24" flexDirection="column" gap="s8">
          <Text size="300">No workflow found with ID {workflowId}</Text>
          <Text size="300">
            Run the test script to create a workflow first, or use ?workflowId= parameter.
          </Text>
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

const PolicyCategoryWorkflowPage = () => {
  return (
    <WorkflowProvider>
      <PolicyCategoryWorkflowPageContent />
    </WorkflowProvider>
  )
}

export default PolicyCategoryWorkflowPage
